import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { directLogout } from "../../../utils/tokenManager";

/**
 * Same 9 sections, same icons, same active-highlight and drawer layout
 * as the approved mobile-admin prototype (drawer sliding from the left,
 * dark navy background, active item highlighted). Real routes/labels
 * verified against AdminSidebar.jsx (desktop) rather than assumed.
 */
const NAV_ITEMS = [
  {
    path: "/dashboard",
    labelKey: "dashboard.dashboard",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="4" y1="20" x2="4" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="20" y1="20" x2="20" y2="14" />
      </svg>
    ),
  },
  {
    path: "/new-client",
    labelKey: "dashboard.addClient",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="16" y1="11" x2="22" y2="11" />
      </svg>
    ),
  },
  {
    path: "/admin/contact-search",
    labelKey: "dashboard.contactSearch",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    path: "/dashboard/new-base",
    labelKey: "dashboard.uploadList",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <polyline points="9 15 12 12 15 15" />
      </svg>
    ),
  },
  {
    path: "/assign-client",
    labelKey: "dashboard.assignClient",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
  },
  {
    path: "/new-user",
    labelKey: "dashboard.addUser",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
      </svg>
    ),
  },
  {
    path: "/client",
    labelKey: "dashboard.scheduledMeetings",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="3" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    path: "/agent",
    labelKey: "dashboard.agentDashboard",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
  },
  {
    path: "/client-dashboard",
    labelKey: "dashboard.dashboardClient",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
];

const AdminMobileTopbar = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
  };

  const goTo = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setDrawerOpen(false);
    try {
      await directLogout();
      sessionStorage.clear();
      localStorage.clear();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <>
      <div className="bg-primary text-white px-4 pt-5 pb-3 flex items-center justify-between relative flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="w-[30px] h-[30px] rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
              <path d="M4 6h16M4 12h16M4 18h10" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <span className="font-extrabold text-sm truncate">{title || "Kikonnect"}</span>
        </div>

        <div className="flex items-center bg-white/15 rounded-full p-0.5 gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => changeLanguage("en")}
            className={`text-[11px] font-bold px-2 py-1 rounded-full ${
              i18n.language === "en" ? "bg-white text-primary" : "text-white/65"
            }`}
          >
            EN
          </button>
          <button
            type="button"
            onClick={() => changeLanguage("es")}
            className={`text-[11px] font-bold px-2 py-1 rounded-full ${
              i18n.language !== "en" ? "bg-white text-primary" : "text-white/65"
            }`}
          >
            ES
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ background: "rgba(8,12,28,0.45)" }}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div
        className="fixed top-0 bottom-0 left-0 z-50 bg-primary flex flex-col transition-transform duration-300"
        style={{
          width: "258px",
          padding: "26px 0 16px",
          transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        <div className="px-[18px] pb-[22px] flex items-center">
          <span className="text-white font-extrabold text-lg">Kikonnect</span>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col px-2 gap-0.5">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => goTo(item.path)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-[13.5px] font-bold ${
                  active ? "bg-white/15 text-white" : "text-white/70"
                }`}
              >
                <span className="w-[17px] h-[17px] flex-shrink-0">{item.icon}</span>
                <span>{t(item.labelKey, item.labelKey)}</span>
              </button>
            );
          })}
        </div>

        <div className="px-2 pt-2.5 mt-1.5" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-[13.5px] font-bold text-red-300 w-full"
          >
            <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
              <path
                d="M15 17v1a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1M9 12h11m0 0-3-3m3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("dashboard.logout")}
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminMobileTopbar;
